import {
  Button,
  Switch,
  MenuItem,
  Select,
  Box,
  InputLabel,
  Typography,
  Tooltip
} from "@mui/material";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "~/app/store";
import {
  selectAutoPingInterval,
  selectAutoPingEnabled,
  selectMaxHistoryEntries,
  selectAllPongHistories,
  setAutoPingInterval,
  setAutoPingEnabled,
  setMaxHistoryEntries,
  clearPongHistory
} from "~/features/andon/andon.slice";
import { hubs } from "~/lib/signalr";
import { HeartbeatEvent } from "~/types/api";

interface PongControlsProps {
  /**
   * Optional additional controls to render alongside the pong controls
   */
  additionalControls?: React.ReactNode;
  /**
   * Whether to show the clear button (default: true)
   */
  showClearButton?: boolean;
}

export default function PongControls({
  additionalControls,
  showClearButton = true
}: PongControlsProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const autoPingEnabled = useAppSelector(selectAutoPingEnabled);
  const autoPingInterval = useAppSelector(selectAutoPingInterval);
  const maxHistoryEntries = useAppSelector(selectMaxHistoryEntries);
  const pongHistories = useAppSelector(selectAllPongHistories);

  const totalEntries = Object.values(pongHistories).reduce(
    (sum, entries) => sum + entries.length,
    0
  );
  const workstationCount = Object.keys(pongHistories).length;
  const oldestEntry = Object.values(pongHistories)
    .flatMap((entries) => entries)
    .reduce<string | null>(
      (oldest, entry) =>
        !oldest || entry.timestamp < oldest ? entry.timestamp : oldest,
      null
    );

  const handlePing = useCallback((targetWorkstationId: string = "") => {
    const pingEvent: HeartbeatEvent.Ping = {
      case: "Ping",
      item: {
        targetWorkstationId,
        timePingSent: new Date()
      }
    };
    hubs.send.next(pingEvent);
  }, []);

  // Auto-ping effect
  useEffect(() => {
    let intervalId: number | null = null;

    if (autoPingEnabled) {
      intervalId = window.setInterval(() => {
        handlePing("");
      }, autoPingInterval);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoPingEnabled, handlePing, autoPingInterval]);

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
    >
      {/* Additional controls slot */}
      {additionalControls}

      {/* Clear button */}
      {showClearButton && (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => dispatch(clearPongHistory())}
          sx={{ whiteSpace: "nowrap" }}
        >
          {t("clear")}
        </Button>
      )}

      {/* Fetch latest button */}
      <Button
        variant="contained"
        onClick={() => handlePing("")}
        sx={{ whiteSpace: "nowrap" }}
      >
        {t("fetch latest")}
      </Button>

      {/* Auto-ping controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          px: 2,
          py: 0.5,
          bgcolor: autoPingEnabled
            ? "background.paper"
            : "action.disabledBackground"
        }}
      >
        <InputLabel sx={{ fontSize: ".8rem" }}>{t("auto ping")}</InputLabel>
        <Switch
          checked={autoPingEnabled}
          onChange={(evt) => dispatch(setAutoPingEnabled(evt.target.checked))}
          size="small"
        />
        <Select
          size="small"
          value={autoPingInterval}
          onChange={(e) =>
            dispatch(setAutoPingInterval(Number(e.target.value)))
          }
          disabled={!autoPingEnabled}
          sx={{ width: 120, ml: 1 }}
        >
          <MenuItem value={1000}>1 second</MenuItem>
          <MenuItem value={2000}>2 seconds</MenuItem>
          <MenuItem value={3000}>3 seconds</MenuItem>
          <MenuItem value={4000}>4 seconds</MenuItem>
          <MenuItem value={5000}>5 seconds</MenuItem>
          <MenuItem value={10000}>10 seconds</MenuItem>
          <MenuItem value={30000}>30 seconds</MenuItem>
          <MenuItem value={60000}>60 seconds</MenuItem>
          <MenuItem value={90000}>90 seconds</MenuItem>
          <MenuItem value={120000}>2 minutes</MenuItem>
          <MenuItem value={300000}>5 minutes</MenuItem>
        </Select>
      </Box>

      {/* History size limit */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          px: 2,
          py: 0.5
        }}
      >
        <InputLabel sx={{ fontSize: ".8rem" }}>{t("history limit")}</InputLabel>
        <Select
          size="small"
          value={maxHistoryEntries}
          onChange={(e) =>
            dispatch(setMaxHistoryEntries(Number(e.target.value)))
          }
          sx={{ width: 120 }}
        >
          <MenuItem value={500}>500</MenuItem>
          <MenuItem value={1000}>1,000</MenuItem>
          <MenuItem value={2500}>2,500</MenuItem>
          <MenuItem value={5000}>5,000</MenuItem>
          <MenuItem value={10000}>10,000</MenuItem>
        </Select>
        <Tooltip
          title={
            oldestEntry
              ? `${workstationCount} workstations · oldest: ${new Date(oldestEntry).toLocaleString()}`
              : "No history data"
          }
        >
          <Typography
            variant="caption"
            sx={{ fontSize: ".7rem", color: "text.secondary", cursor: "help" }}
          >
            {totalEntries.toLocaleString()} entries
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
}
